import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Typography,
  Form,
  Input,
  Button,
  Space,
  Select,
  Row,
  Col,
  message,
  Checkbox,
  Spin,
  Tag,
  Collapse,
  Alert,
} from 'antd';
import { PlusOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, WarningOutlined, CodeOutlined, FormOutlined } from '@ant-design/icons';
import { useAbrangencia } from '../../hooks/useAbrangencia';
import requestPriority from '../../services/requestPriority';
import consultaWeb from '../../services/consultaWeb';
import SourceViewer from '../../components/SourceViewer';
import { SourceProvider } from '../../hooks/useSource';
import type { Platform, Search, Query, Param } from '../../services/abrangencia';

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;

interface MonitoringRequest {
  id: string;
  status: string | null;
  data: any;
  loading: boolean;
  error: string | null;
  notFound: boolean;
  isPending: boolean;
  warningMessage: string | null;
}

/**
 * Página para criar requests com prioridade.
 * 
 * Permite criar requisições selecionando abrangências (platform/search/query),
 * um parâmetro e um valor. Após criar, monitora automaticamente a request.
 */
const RequestCreateContent: React.FC = () => {
  const { data: abrangenciaData, loading: abrangenciaLoading } = useAbrangencia();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [monitoringRequest, setMonitoringRequest] = useState<MonitoringRequest | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [mode, setMode] = useState<'form' | 'json'>('form');
  const [jsonValue, setJsonValue] = useState<string>('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Flatten platforms de todas as sources
  const allPlatforms = useMemo(() => {
    if (!abrangenciaData || abrangenciaData.length === 0) return [];
    const platforms: Platform[] = [];
    abrangenciaData.forEach((source) => {
      source.platforms.forEach((platform) => {
        platforms.push(platform);
      });
    });
    return platforms;
  }, [abrangenciaData]);

  // Obter platform selecionado
  const getPlatform = (platformValue: string | undefined): Platform | undefined => {
    if (!platformValue) return undefined;
    return allPlatforms.find((p) => p.platform === platformValue);
  };

  // Obter search selecionado
  const getSearch = (platformValue: string | undefined, searchValue: string | undefined): Search | undefined => {
    const platform = getPlatform(platformValue);
    if (!platform || !searchValue) return undefined;
    return platform.searches.find((s) => s.search === searchValue);
  };

  // Obter query selecionado
  const getQuery = (
    platformValue: string | undefined,
    searchValue: string | undefined,
    queryValue: string | undefined
  ): Query | undefined => {
    const search = getSearch(platformValue, searchValue);
    if (!search || !queryValue) return undefined;
    return search.queries.find((q) => q.query === queryValue);
  };

  // Obter params disponíveis para uma query
  const getAvailableParams = (
    platformValue: string | undefined,
    searchValue: string | undefined,
    queryValue: string | undefined
  ): Param[] => {
    const query = getQuery(platformValue, searchValue, queryValue);
    if (!query) return [];
    return query.params || [];
  };

  // Valores do formulário atual
  const platformValue = Form.useWatch('platform', form);
  const searchValue = Form.useWatch('search', form);
  const queryValue = Form.useWatch('query', form);
  const paramValue = Form.useWatch('param', form);

  const selectedPlatform = getPlatform(platformValue);
  const selectedSearch = getSearch(platformValue, searchValue);
  const selectedQuery = getQuery(platformValue, searchValue, queryValue);
  const availableParams = getAvailableParams(platformValue, searchValue, queryValue);
  const selectedParam = availableParams.find((p) => p.tag === paramValue);

  // Carregar dados da request para monitoramento
  const loadRequest = useCallback(async (requestId: string) => {
    try {
      const response = await consultaWeb.request({ requestId });

      // Verifica se não foi encontrado (404 ou resposta vazia)
      if (!response || !response.success || !response.requested) {
        // Se não encontrado, continua tentando (pode ainda não ter sido criada no banco)
        setMonitoringRequest({
          id: requestId,
          status: null,
          data: null,
          loading: false,
          error: null,
          notFound: true,
          isPending: true, // Continua monitorando
          warningMessage: null,
        });
        return;
      }

      const status = response.requested?.status || null;
      const isPending = status === "pending";
      
      // Extrair mensagem de warning se existir
      const warningMessage = status === "warning" && response.data 
        ? (typeof response.data === 'string' ? response.data : null)
        : null;

      setMonitoringRequest({
        id: requestId,
        status,
        data: response,
        loading: false,
        error: null,
        notFound: false,
        isPending,
        warningMessage,
      });

      // Se ainda está pendente, continua monitorando
      if (isPending) {
        if (!intervalRef.current) {
          intervalRef.current = setInterval(() => {
            loadRequest(requestId);
          }, 30000); // 30 segundos
        }
      } else {
        // Se não está mais pendente, para o monitoramento
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    } catch (err: any) {
      console.error("Erro ao buscar request:", err);
      
      // Se for erro 404 ou 500, continua tentando (pode ainda não ter sido criada)
      const isNotFound = err.response?.status === 404 || err.response?.status === 500;
      
      setMonitoringRequest({
        id: requestId,
        status: null,
        data: null,
        loading: false,
        error: err.response?.data?.message || err.message || "Erro ao buscar request",
        notFound: isNotFound,
        isPending: isNotFound, // Continua monitorando se não encontrado
        warningMessage: null,
      });

      // Se não encontrado, continua monitorando
      if (isNotFound) {
        if (!intervalRef.current) {
          intervalRef.current = setInterval(() => {
            loadRequest(requestId);
          }, 30000); // 30 segundos
        }
      } else {
        // Para o monitoramento em caso de erro definitivo
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }
  }, []);

  // Cleanup do intervalo quando componente desmonta
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  // Validar e processar JSON
  const validateJson = (jsonString: string): { valid: boolean; data?: any; error?: string } => {
    if (!jsonString || jsonString.trim().length === 0) {
      return { valid: false, error: 'JSON não pode estar vazio' };
    }

    try {
      const parsed = JSON.parse(jsonString);
      
      // Validar campos obrigatórios
      if (!parsed.platform || typeof parsed.platform !== 'string') {
        return { valid: false, error: 'Campo "platform" é obrigatório e deve ser uma string' };
      }
      if (!parsed.search || typeof parsed.search !== 'string') {
        return { valid: false, error: 'Campo "search" é obrigatório e deve ser uma string' };
      }
      if (!parsed.query || typeof parsed.query !== 'string') {
        return { valid: false, error: 'Campo "query" é obrigatório e deve ser uma string' };
      }
      if (!parsed.param || typeof parsed.param !== 'object') {
        return { valid: false, error: 'Campo "param" é obrigatório e deve ser um objeto' };
      }
      if (!parsed.param.key || typeof parsed.param.key !== 'string') {
        return { valid: false, error: 'Campo "param.key" é obrigatório e deve ser uma string' };
      }
      if (!parsed.param.value || typeof parsed.param.value !== 'string') {
        return { valid: false, error: 'Campo "param.value" é obrigatório e deve ser uma string' };
      }

      return { valid: true, data: parsed };
    } catch (e: any) {
      return { valid: false, error: `JSON inválido: ${e.message}` };
    }
  };

  // Formatar JSON
  const beautifyJson = () => {
    const validation = validateJson(jsonValue);
    if (validation.valid && validation.data) {
      setJsonValue(JSON.stringify(validation.data, null, 2));
      setJsonError(null);
    } else {
      message.warning('Corrija os erros do JSON antes de formatar');
    }
  };

  // Atualizar JSON quando o formulário muda (para preview)
  const updateJsonPreview = useCallback(() => {
    const values = form.getFieldsValue();
    const { platform, search, query, param, value, makeDownload, forced, format } = values;

    if (!platform || !search || !query || !param || !value) {
      return;
    }

    const jsonData = {
      platform,
      search,
      query,
      param: {
        key: param,
        value: value.trim(),
      },
      makeDownload: makeDownload || false,
      forced: forced || false,
      format: format || 'default',
    };

    setJsonValue(JSON.stringify(jsonData, null, 2));
  }, [form]);

  // Criar request a partir do JSON
  const handleSubmitJson = async () => {
    const validation = validateJson(jsonValue);
    
    if (!validation.valid) {
      setJsonError(validation.error || 'JSON inválido');
      message.error(validation.error || 'JSON inválido');
      return;
    }

    setJsonError(null);
    setSubmitting(true);

    try {
      const response = await requestPriority.create(98, validation.data!);

      // Verifica se a resposta contém o ID da request
      if (response.success && response.data && response.data.id) {
        const requestId = response.data.id;
        message.success('Requisição criada com sucesso! Iniciando monitoramento...');
        
        // Inicia monitoramento
        setMonitoringRequest({
          id: requestId,
          status: null,
          data: null,
          loading: true,
          error: null,
          notFound: false,
          isPending: true,
          warningMessage: null,
        });

        // Limpar JSON
        setJsonValue('');

        // Inicia monitoramento imediatamente e depois a cada 30s
        loadRequest(requestId);
      } else {
        message.warning('Requisição criada, mas ID não foi retornado. Verifique manualmente.');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro desconhecido';
      message.error('Erro ao criar requisição: ' + errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Criar request a partir do formulário
  const handleSubmit = async (values: any) => {
    const { platform, search, query, param, value, makeDownload, forced, format } = values;

    if (!value || value.trim().length === 0) {
      message.error('Forneça um valor');
      return;
    }

    setSubmitting(true);

    try {
      const response = await requestPriority.create(98, {
        platform,
        search,
        query,
        param: {
          key: param,
          value: value.trim(),
        },
        makeDownload: makeDownload || false,
        forced: forced || false,
        format: format || 'default',
      });

      // Verifica se a resposta contém o ID da request
      if (response.success && response.data && response.data.id) {
        const requestId = response.data.id;
        message.success('Requisição criada com sucesso! Iniciando monitoramento...');
        
        // Inicia monitoramento
        setMonitoringRequest({
          id: requestId,
          status: null,
          data: null,
          loading: true,
          error: null,
          notFound: false,
          isPending: true,
          warningMessage: null,
        });

        // Limpar formulário, mantendo apenas makeDownload, forced e format
        form.setFieldsValue({
          platform: undefined,
          search: undefined,
          query: undefined,
          param: undefined,
          value: undefined,
          makeDownload: makeDownload || false,
          forced: forced || false,
          format: format || 'default',
        });

        // Inicia monitoramento imediatamente e depois a cada 30s
        loadRequest(requestId);
      } else {
        message.warning('Requisição criada, mas ID não foi retornado. Verifique manualmente.');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro desconhecido';
      message.error('Erro ao criar requisição: ' + errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (abrangenciaLoading) {
    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
          <Spin size="large" />
          <Text>Carregando abrangências...</Text>
        </Space>
      </Card>
    );
  }

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={2} style={{ marginBottom: 8 }}>
            <PlusOutlined /> Nova Captura
          </Title>
          <Paragraph style={{ marginBottom: 0 }}>
            Selecione a abrangência (plataforma, busca e query), escolha um parâmetro e forneça o valor.
          </Paragraph>
        </div>

        {/* Toggle entre Form e JSON */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button.Group>
            <Button
              type={mode === 'form' ? 'primary' : 'default'}
              icon={<FormOutlined />}
              onClick={() => setMode('form')}
            >
              Formulário
            </Button>
            <Button
              type={mode === 'json' ? 'primary' : 'default'}
              icon={<CodeOutlined />}
              onClick={() => {
                setMode('json');
                updateJsonPreview();
              }}
            >
              JSON
            </Button>
          </Button.Group>
        </div>

        {mode === 'form' ? (
          <Form
            form={form}
            name="requestCreate"
            onFinish={handleSubmit}
            layout="vertical"
            initialValues={{
              makeDownload: false,
              forced: false,
              format: 'default',
            }}
          >
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="platform"
                label="Plataforma"
                rules={[{ required: true, message: 'Selecione a plataforma' }]}
              >
                <Select
                  showSearch
                  placeholder="Selecione a plataforma"
                  filterOption={(input, option) => {
                    const searchText = input.toLowerCase();
                    const label = String(option?.label ?? '').toLowerCase();
                    const value = String(option?.value ?? '').toLowerCase();
                    return label.includes(searchText) || value.includes(searchText);
                  }}
                  onChange={() => {
                    form.setFieldsValue({
                      search: undefined,
                      query: undefined,
                      param: undefined,
                    });
                  }}
                >
                  {allPlatforms.map((platform) => (
                    <Select.Option key={platform.platform} value={platform.platform} label={`${platform.platform} - ${platform.description}`}>
                      <strong>{platform.platform}</strong> - {platform.description}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="search"
                label="Busca"
                rules={[{ required: true, message: 'Selecione a busca' }]}
              >
                <Select
                  showSearch
                  placeholder="Selecione a busca"
                  disabled={!selectedPlatform}
                  filterOption={(input, option) => {
                    const searchText = input.toLowerCase();
                    const label = String(option?.label ?? '').toLowerCase();
                    const value = String(option?.value ?? '').toLowerCase();
                    return label.includes(searchText) || value.includes(searchText);
                  }}
                  onChange={() => {
                    form.setFieldsValue({
                      query: undefined,
                      param: undefined,
                    });
                  }}
                >
                  {selectedPlatform?.searches.map((search) => (
                    <Select.Option key={search.search} value={search.search} label={`${search.search} - ${search.description}`}>
                      <strong>{search.search}</strong> - {search.description}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="query"
                label="Query"
                rules={[{ required: true, message: 'Selecione a query' }]}
              >
                <Select
                  showSearch
                  placeholder="Selecione a query"
                  disabled={!selectedSearch}
                  filterOption={(input, option) => {
                    const searchText = input.toLowerCase();
                    const label = String(option?.label ?? '').toLowerCase();
                    const value = String(option?.value ?? '').toLowerCase();
                    return label.includes(searchText) || value.includes(searchText);
                  }}
                  onChange={() => {
                    form.setFieldsValue({
                      param: undefined,
                    });
                  }}
                >
                  {selectedSearch?.queries.map((query) => (
                    <Select.Option key={query.query} value={query.query} label={`${query.query} - ${query.description}`}>
                      <strong>{query.query}</strong> - {query.description}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {availableParams.length > 0 && (
            <>
              <Row gutter={16}>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    name="param"
                    label="Parâmetro"
                    rules={[{ required: true, message: 'Selecione o parâmetro' }]}
                  >
                    <Select
                      showSearch
                      placeholder="Selecione o parâmetro"
                      disabled={!selectedQuery}
                      filterOption={(input, option) => {
                        const searchText = input.toLowerCase();
                        const label = String(option?.label ?? '').toLowerCase();
                        const value = String(option?.value ?? '').toLowerCase();
                        return label.includes(searchText) || value.includes(searchText);
                      }}
                    >
                      {availableParams.map((param) => (
                        <Select.Option key={param.tag} value={param.tag} label={`${param.tag} - ${param.label}`}>
                          <strong>{param.tag}</strong> - {param.label}
                          {param.required && <Tag color="red" style={{ marginLeft: 8 }}>Obrigatório</Tag>}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8} md={4}>
                  <Form.Item name="makeDownload" valuePropName="checked" style={{ marginTop: 30 }}>
                    <Checkbox>Make Download</Checkbox>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8} md={4}>
                  <Form.Item name="forced" valuePropName="checked" style={{ marginTop: 30 }}>
                    <Checkbox>Forced</Checkbox>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8} md={4}>
                  <Form.Item name="format" label="Format" style={{ marginTop: 30 }}>
                    <Select>
                      <Select.Option value="default">Default</Select.Option>
                      <Select.Option value="full">Full</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    name="value"
                    label={`Valor ${selectedParam?.required ? '*' : ''} - ${selectedParam?.tag || ''} ${selectedParam?.label ? `(${selectedParam.label})` : ''}`}
                    rules={[
                      { required: true, message: 'Forneça um valor' },
                      {
                        validator: (_: any, value: string) => {
                          if (!value || value.trim().length === 0) {
                            return Promise.reject(new Error('Forneça um valor'));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder={`Digite o valor para ${selectedParam?.tag || 'o parâmetro'}`}
                      style={{ fontFamily: 'monospace' }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button type="primary" htmlType="submit" size="large" block loading={submitting} icon={<PlusOutlined />}>
                  Cadastrar
                </Button>
              </Form.Item>
            </>
          )}

          {queryValue && availableParams.length === 0 && (
            <Form.Item>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Nenhum parâmetro disponível para esta query
              </Text>
            </Form.Item>
          )}
        </Form>
        ) : (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Text strong>JSON:</Text>
              <div style={{ marginTop: 8 }}>
                <TextArea
                  rows={20}
                  value={jsonValue}
                  onChange={(e) => {
                    setJsonValue(e.target.value);
                    setJsonError(null);
                  }}
                  placeholder={`{\n  "platform": "esaj",\n  "search": "tjsp",\n  "query": "unificada",\n  "param": {\n    "key": "cnj",\n    "value": "0000000-00.0000.0.00.0000"\n  },\n  "makeDownload": false,\n  "forced": false,\n  "format": "default"\n}`}
                  style={{ fontFamily: 'monospace' }}
                />
              </div>
              {jsonError && (
                <Alert
                  message="Erro no JSON"
                  description={jsonError}
                  type="error"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </div>
            <Space>
              <Button onClick={beautifyJson}>Formatar JSON</Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleSubmitJson}
                loading={submitting}
              >
                Cadastrar
              </Button>
            </Space>
          </Space>
        )}

        {/* Monitoramento da Request */}
        {monitoringRequest && (
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={4}>Monitoramento da Request</Title>
                {monitoringRequest.isPending && (
                  <Alert
                    message="Monitoramento Ativo"
                    description="A request está sendo monitorada a cada 30 segundos. Continue aguardando até que o status mude de 'pending'."
                    type="info"
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>

              <Collapse>
                <Panel
                  header={
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                      <Text copyable code style={{ fontSize: "14px" }}>
                        {monitoringRequest.id}
                      </Text>
                      {monitoringRequest.loading ? (
                        <>
                          <Spin size="small" />
                          <Text type="secondary">Buscando...</Text>
                        </>
                      ) : monitoringRequest.notFound ? (
                        <>
                          <Tag color="processing" icon={<ClockCircleOutlined />}>
                            Aguardando criação...
                          </Tag>
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            A request ainda não foi encontrada. Continuando a buscar a cada 30s...
                          </Text>
                        </>
                      ) : monitoringRequest.status ? (
                        <>
                          <Tag 
                            color={
                              monitoringRequest.status === "success" 
                                ? "success" 
                                : monitoringRequest.status === "error" 
                                ? "error" 
                                : monitoringRequest.status === "warning"
                                ? "warning"
                                : "processing"
                            }
                            icon={
                              monitoringRequest.status === "success" 
                                ? <CheckCircleOutlined /> 
                                : monitoringRequest.status === "error" 
                                ? <CloseCircleOutlined />
                                : monitoringRequest.status === "warning"
                                ? <WarningOutlined />
                                : <ClockCircleOutlined />
                            }
                          >
                            {monitoringRequest.status}
                          </Tag>
                          {monitoringRequest.warningMessage && (
                            <Text type="warning" style={{ fontSize: "13px" }}>
                              {monitoringRequest.warningMessage}
                            </Text>
                          )}
                          {monitoringRequest.isPending && (
                            <Tag color="processing">
                              Monitorando...
                            </Tag>
                          )}
                        </>
                      ) : (
                        <Tag color="error">Erro</Tag>
                      )}
                    </div>
                  }
                  key={monitoringRequest.id}
                >
                  {monitoringRequest.data ? (
                    <SourceViewer sourceId={monitoringRequest.id} />
                  ) : monitoringRequest.error ? (
                    <Text type="danger">{monitoringRequest.error}</Text>
                  ) : (
                    <Text type="secondary">Aguardando dados da request...</Text>
                  )}
                </Panel>
              </Collapse>
            </Space>
          </Card>
        )}
      </Space>
    </Card>
  );
};

/**
 * Componente principal da página RequestCreate.
 */
const RequestCreate: React.FC = () => {
  return (
    <SourceProvider>
        <RequestCreateContent />
      </SourceProvider>
  );
};

export default RequestCreate;
